// Lectura de QR desde la cámara. Usa el lector nativo del navegador (BarcodeDetector: Chrome
// en Android) y, si no lo hay (Safari en iPhone, Firefox), jsQR, que se descarga solo al
// escanear. Devuelve el texto del QR del fotograma actual o null si no ve ninguno.

export type QrReader = (video: HTMLVideoElement) => Promise<string | null>

type DetectedBarcode = { rawValue: string }
type BarcodeDetectorLike = { detect(source: HTMLVideoElement): Promise<DetectedBarcode[]> }
export type BarcodeDetectorConstructor = {
	new (options: { formats: string[] }): BarcodeDetectorLike
	getSupportedFormats(): Promise<string[]>
}
type JsQr = (data: Uint8ClampedArray, width: number, height: number, options?: object) => { data: string } | null
type CanvasLike = { width: number; height: number; getContext(type: '2d', options?: object): Ctx2d | null }
type Ctx2d = Pick<CanvasRenderingContext2D, 'drawImage' | 'getImageData'>

export type QrReaderDeps = {
	BarcodeDetector?: BarcodeDetectorConstructor
	loadJsQr: () => Promise<JsQr>
	createCanvas: () => CanvasLike
}

// Lado máximo del fotograma que se analiza con jsQR: más es lento en móviles modestos y no
// mejora la lectura de un QR a la distancia de un brazo.
export const MAX_FRAME_SIDE = 640

export const frameSize = (width: number, height: number, max = MAX_FRAME_SIDE) => {
	if (width <= 0 || height <= 0) return null
	const scale = Math.min(1, max / Math.max(width, height))
	return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

const defaultDeps = (): QrReaderDeps => ({
	BarcodeDetector: (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector,
	loadJsQr: () => import('jsqr').then((module) => module.default as unknown as JsQr),
	createCanvas: () => document.createElement('canvas'),
})

const nativeReader = async (Detector: BarcodeDetectorConstructor | undefined): Promise<QrReader | null> => {
	try {
		if (!Detector || !(await Detector.getSupportedFormats()).includes('qr_code')) return null
		const detector = new Detector({ formats: ['qr_code'] })
		return async (video) => (await detector.detect(video))[0]?.rawValue ?? null
	} catch {
		return null
	}
}

const jsQrReader = async (deps: QrReaderDeps): Promise<QrReader> => {
	const jsQR = await deps.loadJsQr()
	const canvas = deps.createCanvas()
	const ctx = canvas.getContext('2d', { willReadFrequently: true })
	return async (video) => {
		const size = frameSize(video.videoWidth, video.videoHeight)
		if (!ctx || !size) return null
		canvas.width = size.width
		canvas.height = size.height
		ctx.drawImage(video, 0, 0, size.width, size.height)
		const { data } = ctx.getImageData(0, 0, size.width, size.height)
		return jsQR(data, size.width, size.height, { inversionAttempts: 'dontInvert' })?.data ?? null
	}
}

export const createQrReader = async (deps: QrReaderDeps = defaultDeps()): Promise<QrReader> =>
	(await nativeReader(deps.BarcodeDetector)) ?? jsQrReader(deps)
